import React, { useState, useRef, useEffect } from 'react'
import UserContext from "../userContext"
import logo from '../images/mypacomefavicon.svg'
import { Button, Modal } from 'react-bootstrap'
import { FiWifiOff, FiUser, FiSettings, FiLogOut } from 'react-icons/fi'
import Message from './Components/Modal/Message'

export default function Header(props) {
    const userContext = React.useContext(UserContext)
    const [showModal, setShowModal] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const [message, setMessage] = useState(null)
    const dropdownRef = useRef(null)

    const handleClose = () => setShowModal(false)
    const handleShow = () => setShowModal(true)

    // La popup de succès recharge la page à sa fermeture, comme le faisait l'alert() avant elle
    const closeMessage = () => {
        const recharger = message?.reloadOnClose
        setMessage(null)
        if (recharger) window.location.reload()
    }

    // Sauvegarde hors ligne en attente : le modal liste l'entrée, la sortie ou le rapport
    let localInventory = null
    try {
        localInventory = JSON.parse(localStorage.getItem('savedFormData'))
        if (localInventory?.inventory) {
            localInventory = JSON.parse(localInventory.inventory)
        }
    } catch (err) {
        console.error('Sauvegarde hors ligne illisible:', err)
    }

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Photo remise en fichier depuis la base64 gardée dans le localStorage
    const fichierDepuisSauvegarde = (photo) => {
        const [entete, donnees] = String(photo.content || '').split(',');
        const type = photo.type || entete?.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
        const binaire = atob(donnees || '');
        const octets = new Uint8Array(binaire.length);
        for (let i = 0; i < binaire.length; i++) {
            octets[i] = binaire.charCodeAt(i);
        }
        return new File([new Blob([octets], { type })], photo.name, { type });
    };

    // Renvoi de la validation gardée hors ligne : état des lieux ou rapport, avec ses photos et ses heures
    const validateAndSendInventory = async () => {
        let sauvegarde = null;
        try {
            sauvegarde = JSON.parse(localStorage.getItem('savedFormData'));
        } catch (err) {
            console.error('Sauvegarde hors ligne illisible:', err);
        }
        if (!sauvegarde) {
            setShowModal(false);
            setMessage({ title: 'Rien en attente', body: "Il n'y a plus rien à envoyer.", variant: 'info' });
            return;
        }
        if (!navigator.onLine) {
            setMessage({ title: 'Vous êtes hors ligne', body: 'Réessayez quand vous serez de nouveau connecté.', variant: 'info' });
            return;
        }
        try {
            const fd = new FormData();
            fd.append('id', sauvegarde.id);
            if (sauvegarde.inventory) fd.append('inventory', sauvegarde.inventory);
            if (sauvegarde.report) fd.append('report', sauvegarde.report);
            if (sauvegarde.workedDuration) fd.append('workedDuration', sauvegarde.workedDuration);
            for (const photo of sauvegarde.files || []) {
                if (photo.content) fd.append('files', fichierDepuisSauvegarde(photo));
            }
            await userContext.apiReducer('validate', fd);
            localStorage.removeItem('savedFormData');
            setShowModal(false);
            setMessage({
                title: 'Envoi réussi',
                body: sauvegarde.inventory ? "L'état des lieux a bien été envoyé." : 'Le rapport a bien été envoyé.',
                variant: 'success',
                reloadOnClose: true,
            });
        } catch (error) {
            console.error("Erreur pendant l'envoi:", error);
            setMessage({
                title: 'Envoi impossible',
                body: "L'envoi n'a pas abouti. Votre validation est toujours en attente, réessayez dans un instant.",
                variant: 'danger',
            });
        }
    };

    return (
        <>
            <header className="app-header">
                <nav className="navbar">
                    <a href="/" className="navbar-brand">
                        <img src={logo} alt="MYPacôme" className="brand-logo" />
                        <div className="brand-info">
                            <span className="brand-text">MYPacôme</span>
                            <span className="user-name">{userContext.user?.name} {userContext.user?.lastName}</span>
                        </div>
                    </a>

                    <div className="navbar-actions">
                        {localInventory && (
                            <button className="offline-btn" onClick={handleShow}>
                                <FiWifiOff />
                                <span className="btn-text">En attente</span>
                            </button>
                        )}

                        <div className="user-menu" ref={dropdownRef}>
                            <button
                                className="avatar-btn"
                                onClick={() => setShowDropdown(!showDropdown)}
                            >
                                <img src={logo} alt="Profile" className="user-avatar" />
                            </button>

                            <div className={`dropdown-menu ${showDropdown ? 'show' : ''}`}>
                                <button className="dropdown-item" disabled>
                                    <FiUser />
                                    <span>Profil</span>
                                </button>
                                <button className="dropdown-item" disabled>
                                    <FiSettings />
                                    <span>Paramètres</span>
                                </button>
                                <div className="dropdown-divider" />
                                <button
                                    className="dropdown-item dropdown-item--danger"
                                    onClick={() => userContext.logout()}
                                >
                                    <FiLogOut />
                                    <span>Déconnexion</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>

            <Modal show={showModal} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiWifiOff className="me-2" />
                        États des lieux en attente
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Les éléments suivants seront envoyés dès que vous aurez une connexion internet :</p>
                    <ul className="pending-list">
                        {localInventory?.entree && (
                            <li>
                                <strong>Entrée</strong><br />
                                {localInventory.entree['in-start']}<br />
                                <small>{localInventory.entree['in-property-address']}</small>
                            </li>
                        )}
                        {localInventory?.sortie && (
                            <li>
                                <strong>Sortie</strong><br />
                                {localInventory.sortie['out-start']}<br />
                                <small>{localInventory.sortie['out-property-address']}</small>
                            </li>
                        )}
                        {localInventory?.report && (
                            <li>
                                <strong>Rapport</strong><br />
                                {localInventory.report}
                            </li>
                        )}
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={handleClose}>
                        Fermer
                    </Button>
                    <Button variant="primary" onClick={validateAndSendInventory}>
                        Envoyer maintenant
                    </Button>
                </Modal.Footer>
            </Modal>

            <Message message={message} onClose={closeMessage} />
        </>
    )
}
